import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'state';
import styled from 'styled-components';

import { signin } from 'actions/auth';
import useServices from 'app/services';
import logger from 'utils/logger';

import ErrorComponent from 'app/ui/components/atoms/error';
import LoadingWithOverlay from 'app/ui/components/molecules/loading-with-overlay';
import HtmlTitle from 'src/app/ui/components/atoms/html-title';

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

const PortalAuthorizationPage = () => {
  const services = useServices();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleTokenExchange = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          logger.error({ message: 'No Cognito authorization token provided' });
          return setError('No authorization token provided');
        }

        const tokenPayload = await services.cognito.exchangeCognitoToken(token);

        if (tokenPayload?.accessToken) {
          dispatch(signin(tokenPayload));

          // Sub-plan S: the hyrox-api mirror session comes from the same Cognito token. mint() never
          // rejects and reports its own failures, so a mirror problem cannot affect the login.
          services.hyroxMirror?.session.mint(token);

          // Check for redirect parameters
          const params = new URLSearchParams(location.search);
          const redirectTo = params.get('redirect_to');
          const verifyRedirectTo = params.get('verify_redirect_to');

          if (redirectTo) {
            const decodedRedirect = decodeURIComponent(redirectTo);
            setTimeout(() => {
              window.location.href = decodedRedirect;
            }, 500);
          } else if (verifyRedirectTo) {
            // Handle verify-onefiit redirect
            const decodedRedirect = decodeURIComponent(verifyRedirectTo);
            setTimeout(() => {
              window.location.href = decodedRedirect;
            }, 500);
          } else {
            setTimeout(() => navigate('/'), 500);
          }
        }
      } catch (error) {
        logger.error({ message: 'Failed to authenticate with the portal', error });
        setError('Failed to authenticate with the portal');
      }
    };

    handleTokenExchange();
  }, [services, dispatch, location.search, navigate]);

  return (
    <>
      <HtmlTitle title="Portal Authorization" />
      <Layout>{error ? <ErrorComponent /> : <LoadingWithOverlay />}</Layout>
    </>
  );
};

export default PortalAuthorizationPage;
